import React from "react";
import NewLayout from "@components/new-ui/Layout";
import PaymentStepsAccordion, { PaymentProcess } from "@components/integration/PaymentStepsAccordian";
import PaymentStepsCard, { PaymentSectionDataProps } from "@components/integration/PaymentStepsCard";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import FeatureInfoCard, { FeatureInfo } from "@components/new-ui/IndustriesSection/FeatureInfoCard";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import ProductSection, { Product } from "@components/new-ui/IndustriesSection/ProductSection";
import faqSchema from "components/faqSchema/whatsapp-broadcast.json";
import "keen-slider/keen-slider.min.css";
import { KeenSliderPlugin, useKeenSlider } from "keen-slider/react";
import Head from "next/head";
import Image from "next/image";
import { GoArrowUpRight } from "react-icons/go";
import ExternalLinkButton from "@components/new-ui/ExternalLinkButton";
import { PiCaretCircleLeftThin, PiCaretCircleRightThin } from "react-icons/pi";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import IntegrationSection from "@components/new-ui/IntegrationSection";
import AwardsSection from "@components/new-ui/AwardsSection";
import CTASection from "@components/new-ui/CTASection";

interface ComparisonData {
    imageUrl: string;
    title: string;
    description: string;
    externalLink: string;
}

export interface Feature {
    mediaUrl: string;
    title: string;
    description?: string;
    url: string;
}

const herosectionData: HerosectionProps = {
    chipTitle: "",
    title: "Maximize outreach with our WhatsApp Broadcast software",
    description: "Amplify reach with segmentation, personalization, and automation — delivered with a human touch.",
    primaryButtonText: "Try for free",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/hero-image.png",
};

const featureInfo1: FeatureInfo = {
    mainTitle: "Connect with your ideal audience",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/select-audience.png",
    featureInfoList: [
        "Upload contact lists or filter groups by opt-ins, tags, or demographics.",
        "Use Meta-approved templates to create relevant content that resonates with each audience.",
        "Verify your message with up to 10 sample contacts to ensure perfect delivery.",
    ],
};

const featureInfo2: FeatureInfo = {
    mainTitle: "Messages that land, every time",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/select-template.png",
    featureInfoList: [
        "Auto-resend undelivered messages (3 attempts) on a chosen date.",
        "Plan campaigns to be sent at optimal times for maximum impact.",
        "Adjust or cancel broadcasts anytime.",
    ],
};

const featureInfo3: FeatureInfo = {
    mainTitle: "Approved templates for every need",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/template-approval.png",
    featureInfoList: [
        "Ready-to-use templates for notifications, alerts, and updates.",
        "Choose from utility (billing alerts), marketing (promotions), or authentication (OTPs) to match your goal.",
        "Insert customer-specific details so your messages feel personalized.",
    ],
};

const featureInfo4: FeatureInfo = {
    mainTitle: "Your workflow, optimized",
    imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/workflow.png",
    featureInfoList: [
        "Create, monitor, and manage all broadcasts via a single dashboard.",
        "Automatically direct replies to the right team with smart routing.",
        "Reassign conversations instantly to balance workload.",
    ],
};

const productCardData: Array<Product> = [
    {
        title: "Higher opens, replies & conversions ",
        description: "Send personalized messages at scale by segmenting your audience — watch engagement soar beyond email and SMS.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/group-chat.svg",
        headerBackground: true,
    },
    {
        title: "Launch campaigns in minutes",
        description: "Create, schedule, and track all your messages from one dashboard — no manual work, just fast and effective outreach.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/campaign.svg",
        headerBackground: true,
    },
    {
        title: "Optimize campaigns on the fly",
        description: "Test messages before sending, track key metrics, and refine your strategy to maximize results — while being compliant.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/data-analysis.svg",
        headerBackground: true,
    },
    {
        title: "Instant updates & promotions",
        description: "Leverage WhatsApp’s high open rates for urgent alerts, and keep conversations flowing with seamless two-way chats.",
        imageUrl: "https://assets.gallabox.com/gb-home/solution/solution-icons/inbox-unread.svg",
        headerBackground: true,
    },
];

const paymentSectionData: PaymentSectionDataProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/brodcast.png",
        title: "Access broadcast feature",
        description: "Log in to your Gallabox account, navigate to the Broadcast section in the dashboard.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/selct-audience-carosel.png",
        title: "Select contacts",
        description: "Create a new broadcast list by selecting contacts.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/craft-message.png",
        title: "Craft message",
        description: "Compose your message and customize it as needed.",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/broadcast.png",
        title: "Send or schedule ",
        description: "Schedule or send the broadcast immediately.",
    },
];

const paymentAccordianData: PaymentProcess = {
    title: "How to get started?",
    description: "To get started with the WhatsApp Broadcast feature in Gallabox, follow these steps:",
    ProcessSteps: [
        {
            imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/brodcast.png",
            title: "Access broadcast feature",
            description: "Log in to your Gallabox account, navigate to the Broadcast section in the dashboard.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/selct-audience-carosel.png",
            title: "Select contacts",
            description: "Create a new broadcast list by selecting contacts.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/craft-message.png",
            title: "Craft message",
            description: "Compose your message and customize it as needed.",
        },
        {
            imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/broadcast.png",
            title: "Send or schedule ",
            description: "Schedule or send the broadcast immediately.",
        },
    ],
};

const comparisonData: ComparisonData[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/qic.png",
        title: "How Gallabox Helped Qatar Insurance Company Generate 2X More Leads on WhatsApp",
        description: "From doubling leads to improving CSAT – find out how Qatar Insurance Company transformed their WhatsApp Business automation game with Gallabox.",
        externalLink: "https://gallabox.com/case-study/qatar-insurance-company",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/bitter.png",
        title: "Maximizing Conversions and Streamlining Support: Hugg's Transformation with Gallabox",
        description: "Here's the awe-inspiring tale of how Hugg boosted efficiency, enhanced customer support, and multiplied conversion rates with Gallabox.",
        externalLink: "https://gallabox.com/case-study/hugg",
    },
];

const sampleData: Feature[] = [
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/right-whatsapp-template.png",
        title: "Choosing the Right WhatsApp Template",
        url: "https://www.youtube.com/watch?v=3I_uV1CRgxE",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/create-segment.png",
        title: "Create Targeted Segments",
        url: "https://www.youtube.com/watch?v=yBpV_llptOw",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/contact-excel.png",
        title: "Uploading Contacts from Excel",
        url: "https://www.youtube.com/watch?v=wGqLXUJpkBQ",
    },
    {
        mediaUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/broadcast-campaign.png",
        title: "Schedule your Broadcast Campaign",
        url: "https://www.youtube.com/watch?v=kPdVsJpgx1I",
    },
];

const defaultFAQData: FAQSectionProps = {
    title: "FAQs for WhatsApp Broadcast Software",
    faqItems: [
        {
            question: "How does contact segmentation improve broadcast response rates?",
            answer: (
                <div>
                    <p>
                        Contact segmentation boosts response rates by letting you send laser-targeted messages to engaged audiences. Focus on users who’ve opted in (see exactly
                        who’s eligible!), avoid spamming opt-outs, and watch open rates climb. Stay compliant, save time, and send content that truly resonates—because relevance
                        drives replies.
                    </p>
                </div>
            ),
        },
        {
            question: "Can I automate replies and assign conversations to specific teams?",
            answer: (
                <div>
                    Yes, Gallabox’s Reply Settings let you auto-assign chats to teams or bots seamlessly. Set up canned responses to instantly route customer replies (e.g., to your
                    “Sales” team), ditch manual handoffs, and keep conversations flowing. Override old assignments with a click — no chaos, just smart, hassle-free teamwork.
                </div>
            ),
        },
        {
            question: "How do abandoned cart reminders help recover lost sales?",
            answer: (
                <p>
                    Yes, abandoned cart reminders recover lost sales by using:
                    <br />
                    <ul>
                        <li>Chatbots to trigger instant, personalized nudges.</li>
                        <li>Broadcasts for scheduled, bulk follow-ups.</li>
                        <li>
                            Drip campaigns (multi-step reminders) to gently re-engage. Tailored messages + incentives (discounts/urgency) boost conversions while balancing
                            persistence and CX.
                        </li>
                    </ul>
                </p>
            ),
        },
        {
            question: "Is there a limit to products per catalog message template?",
            answer: (
                <div>
                    For Catalog Message Templates, there is no explicitly stated limit to the number of products that can appear. In contrast, Multi-Product Message (MPM) Templates
                    allow you to display up to 30 different items (organized into up to 10 sections) in a single message.
                </div>
            ),
        },
        {
            question: "What metrics does Gallabox track to measure campaign ROI?",

            answer: (
                <div>
                    Gallabox serves up real-time campaign stats which allows you to track views, replies, reactions, link clicks, and unsubscribes. Crunch the numbers, spot what’s
                    working (or not), and tweak your strategy, if required. No guesswork — just clear wins.
                </div>
            ),
        },
        {
            question: "How does Gallabox simplify lead qualification processes?",
            answer: (
                <div>
                    Gallabox simplifies lead qualification by automating the process when users engage with Click-to-WhatsApp (CTWA) ads — instantly marking them as leads — while
                    allowing manual customization via labels (e.g., tagging “Interested”). This dual approach prioritizes high-potential leads, enabling focused follow-ups,
                    optimized marketing strategies, and reduced manual effort. Effortlessly streamline lead management without complex setups.
                </div>
            ),
        },
        {
            question: "Can I schedule drip campaigns for WhatsApp broadcasts?",
            answer: (
                <p>
                    Yes, you can use a WhatsApp automation platform such as Gallabox to schedule WhatsApp drip campaigns in Gallabox: Go to Sequences, create a new sequence, name
                    it, select your audience (auto-set as contacts), choose a WhatsApp number, and finalize setup. Automate broadcasts to engage audiences efficiently.
                </p>
            ),
        },
        {
            question: "Does Gallabox integrate with e-commerce platforms or CRMs?",
            answer: (
                <p>
                    Yes, Gallabox seamlessly integrates with popular e-commerce platforms like Shopify, WooCommerce, and CartFlow, as well as leading CRMs such as HubSpot, Kylas
                    CRM, Zoho CRM, and LeadSquared. These no-code connections enable automated workflows, syncing customer data and interactions between your store/CRM and
                    WhatsApp, streamlining communication and order management without requiring technical setup.
                </p>
            ),
        },
        {
            question: "What’s included in the free trial? Any hidden costs?",
            answer: (
                <p>
                    The 7-day free trial does not require a credit card. You’ll have access to all tools (chatbots, automations) and receive free conversation credits. No hidden
                    charges—the trial expires automatically after 7 days.
                </p>
            ),
        },
        {
            question: "Is technical expertise needed to set up Gallabox?",
            answer: (
                <div>
                    No coding needed. Gallabox is a no-code platform for SMBs to automate WhatsApp workflows via a visual interface. Build chatbots, manage broadcasts, and handle
                    customer conversations without technical skills.
                </div>
            ),
        },
    ],
};
const WhatsappBroadcast: React.FC = () => {
    return (
        <NewLayout
            title="Whatsapp Broadcast Software to Send Bulk Messages Easily"
            description="Boost engagement with Gallabox's WhatsApp Broadcast Software! Easily send bulk messages, automate responses & drive conversions. Try it today!"
            canonical="https://gallabox.com/whatsapp-broadcast"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/wa-broadcast/wa-broadcast-og.png" }}
            schema={{
                heroOrOgImageAlt: "whatsapp-broadcast-image",
                heroOrOgImageDescription: "Amplify reach with segmentation, personalization, and automation — delivered with a human touch.",
                heroOrOgImageUrl: "https://assets.gallabox.com/gb-home/wa-broadcast/hero-image.png",
                softwareFeatureName: "whatsapp-broadcast",
                howTo: { howtoSteps: paymentAccordianData.ProcessSteps },
            }}
        >
            <Head>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </Head>
            <Herosection herosection={herosectionData} />
            <div className="lg:max-w-7xl mx-auto lg:pt-0 lg:pb-10 py-14">
                <p className="text-base text-center font-normal py-2">Trusted by 10,000+ businesses</p>
                <ClientLogos />
            </div>
            <div className={`${container_classname} pt-20`}>
                <h4 className="md:text-7xl text-4xl">Key features and functionalities of WhatsApp Broadcast software</h4>
            </div>
            <section>
                <FeatureInfoCard {...featureInfo1} />
                <FeatureInfoCard {...featureInfo2} />
                <FeatureInfoCard {...featureInfo3} />
                <FeatureInfoCard {...featureInfo4} />
            </section>
            <ProductSection title="Benefits of using Gallabox’s WhatsApp Broadcast software" productCardData={productCardData} />
            <section className="lg:block hidden">
                <PaymentStepsCard
                    title={"How to get started?"}
                    backgroundColor=""
                    description="To get started with the WhatsApp Broadcast feature in Gallabox, follow these steps:"
                    paymentSectionData={paymentSectionData}
                />
            </section>
            <PaymentStepsAccordion paymentStepsData={paymentAccordianData} />
            <section className={`${container_classname} lg:py-20 pt-12 pb-20 relative`}>
                <h2 className="md:text-6xl text-3xl font-semibold" style={{ fontFamily: "Degular Display" }}>
                    Why fast-growing businesses love Gallabox
                </h2>

                <p className="mb-12 mt-2">Take a look at the accolades and awards Gallabox has scored from G2 as proof of the love customers have for our product.</p>

                <div className="w-full flex lg:flex-row flex-col lg:gap-5 gap-6">
                    {comparisonData.map((data, index) => {
                        const { imageUrl, title, description } = data;
                        return (
                            <a key={index} className="lg:w-1/2 w-full lg:mx-0 mx-auto" href="">
                                <Image width={610} height={338} src={imageUrl} alt={title} />
                                <div className="lg:px-6 px-4 lg:py-8 py-5 border border-solid border-gray-200 border-t-0 rounded-b-lg">
                                    <h6 className="lg:text-xl text-xl font-semibold mb-6">{title}</h6>
                                    <p className="lg:text-lg text-base no-underline">{description}</p>
                                    <GoArrowUpRight size={20} className="mt-5 ml-auto" />
                                </div>
                            </a>
                        );
                    })}
                </div>
            </section>
            <FeaturesSection />
            <FAQSection faqData={defaultFAQData} />
            <IntegrationSection /> <AwardsSection />
            <CTASection title="Ready to Elevate Your Marketing Strategy?" description="Experience the power of Gallabox's WhatsApp drip marketing provider firsthand" />
        </NewLayout>
    );
};

export default WhatsappBroadcast;

export const WheelControls: KeenSliderPlugin = (slider) => {
    let touchTimeout: ReturnType<typeof setTimeout>;
    let position: {
        x: number;
        y: number;
    };
    let wheelActive: boolean;

    function dispatch(e: WheelEvent, name: string) {
        position.x -= e.deltaY;
        position.y -= e.deltaX;
        slider.container.dispatchEvent(
            new CustomEvent(name, {
                detail: {
                    x: position.x,
                    y: position.y,
                },
            })
        );
    }

    function wheelStart(e: WheelEvent) {
        position = {
            x: e.pageX,
            y: e.pageY,
        };
        dispatch(e, "ksDragStart");
    }

    function wheel(e: WheelEvent) {
        dispatch(e, "ksDrag");
    }

    function wheelEnd(e: WheelEvent) {
        dispatch(e, "ksDragEnd");
    }

    function eventWheel(e: WheelEvent) {
        e.preventDefault();
        if (!wheelActive) {
            wheelStart(e);
            wheelActive = true;
        }
        wheel(e);
        clearTimeout(touchTimeout);
        touchTimeout = setTimeout(() => {
            wheelActive = false;
            wheelEnd(e);
        }, 50);
    }

    slider.on("created", () => {
        slider.container.addEventListener("wheel", eventWheel, {
            passive: false,
        });
    });
};

const FeatureCard: React.FC<Feature> = (props) => {
    return (
        <div className={"keen-slider__slide  flex flex-col gap-0 rounded"}>
            <div className="w-full rounded-t-lg overflow-hidden lg:h-64 h-44">
                <Image src={props.mediaUrl} alt={props.title} width={600} height={600} style={{ width: "100%", height: "100%", objectFit: "fill" }} />
            </div>
            <div className="w-full h-32 bg-white p-6 rounded-b-lg flex flex-col">
                <h3 className="text-xl font-semibold mb-2" color="#151515">
                    {props.title}
                </h3>

                {props.description && <p className="text-base mb-3 w-fit">{props.description}</p>}
                <div className="flex-grow" />
                <ExternalLinkButton size="sm" href={props.url}>
                    Watch Video
                </ExternalLinkButton>
            </div>
        </div>
    );
};

export const FeaturesSection: React.FC<{ features?: Feature[]; title?: string; description?: string }> = ({
    features = sampleData,
    title = "Learn and launch your broadcasts",
    description,
}) => {
    const [currentSlide, setCurrentSlide] = React.useState(0);
    const [loaded, setLoaded] = React.useState(false);
    const [sliderRef, instanceRef] = useKeenSlider({
        mode: "free",
        drag: true,
        // rubberband: true,
        vertical: false,
        slides: {
            perView: 2.5,
            spacing: 20,
        },
        breakpoints: {
            "(max-width: 640px)": {
                slides: { perView: 1.1, spacing: 20 },
            },
        },
        initial: 0,
        slideChanged(slider) {
            setCurrentSlide(slider.track.details.rel);
        },
        created() {
            setLoaded(true);
        },
    });

    const disableLeftArrow = React.useMemo(() => currentSlide === 0, [currentSlide]);
    const disableRightArrow = React.useMemo(() => {
        const slidesLength = features.length;
        return slidesLength !== undefined && currentSlide === slidesLength - 1;
    }, [currentSlide, features]);

    return (
        <section
            className="lg:py-20 py-16"
            style={{
                background: "linear-gradient(103deg, #EDF4FF 28.71%, #D2DDFF 84.45%, #84A2FF 136.43%)",
            }}
        >
            <div className={`${container_classname} flex flex-col lg:gap-12 gap-8`}>
                <div className="flex justify-between">
                    <div className="flex flex-col gap-6">
                        <h2 className={"lg:text-5xl text-3xl font-semibold text-black lg:max-w-5xl"} style={{ fontFamily: "Degular Display" }}>
                            {title}
                        </h2>
                        {description && <p>{description}</p>}
                    </div>
                    {loaded && instanceRef.current && (
                        <div className="lg:flex hidden text-white">
                            <PiCaretCircleLeftThin
                                style={{ cursor: disableLeftArrow ? "not-allowed" : "pointer" }}
                                size={60}
                                onClick={(e: any) => {
                                    if (!disableLeftArrow) e.stopPropagation() || instanceRef.current?.prev();
                                }}
                                color="black"
                            />
                            <PiCaretCircleRightThin
                                style={{ cursor: disableRightArrow ? "not-allowed" : "pointer" }}
                                size={60}
                                onClick={(e: any) => {
                                    if (!disableRightArrow) e.stopPropagation() || instanceRef.current?.next();
                                }}
                                color="black"
                            />
                        </div>
                    )}
                </div>
                <div ref={sliderRef} className="keen-slider w-full" style={{ overflow: "visible" }}>
                    {features.map((feature, index) => (
                        <FeatureCard key={index} {...feature} />
                    ))}
                </div>
                {loaded && instanceRef.current && (
                    <div className="lg:hidden mx-auto flex gap-1 text-white">
                        <PiCaretCircleLeftThin
                            style={{ cursor: disableLeftArrow ? "not-allowed" : "pointer" }}
                            size={60}
                            onClick={(e: any) => {
                                if (!disableLeftArrow) e.stopPropagation() || instanceRef.current?.prev();
                            }}
                            color="black"
                        />
                        <PiCaretCircleRightThin
                            style={{ cursor: disableRightArrow ? "not-allowed" : "pointer" }}
                            size={60}
                            onClick={(e: any) => {
                                if (!disableRightArrow) e.stopPropagation() || instanceRef.current?.next();
                            }}
                            color="black"
                        />
                    </div>
                )}
            </div>
        </section>
    );
};
